// src/components/QuizFelder.jsx
//
// Einstellungen einer Quiz-Station im Anlegen- und Bearbeiten-Dialog:
// Spielzeit fuer Erwachsene und Kinder, Abzug fuer falsche Antworten,
// gemischte Reihenfolge, Vorlesen (Sprachausgabe) je Kategorie. Die Fragen selbst und
// die PIN pflegt der Admin unter „Quiz“ (pages/QuizVerwaltung.jsx).

import { useState } from "react";
import Input from "./ui/Input";
import { tempoFuer, vorlesenTesten, vorlesenVerfuegbar } from "../utils/vorlesen";

export const QUIZ_STANDARD = Object.freeze({ sekunden: "120", sekundenKinder: "240", abzug: false, abzugKinder: false, mischen: false, vorlesen: false, vorlesenKinder: false });

/** Formularzustand aus einer bestehenden Station. */
export function quizVon(station) {
  return {
    sekunden: station?.quiz_seconds ? String(station.quiz_seconds) : QUIZ_STANDARD.sekunden,
    sekundenKinder: station?.quiz_seconds_kids ? String(station.quiz_seconds_kids) : QUIZ_STANDARD.sekundenKinder,
    abzug: station?.quiz_penalty === true,
    abzugKinder: station?.quiz_penalty_kids === true,
    mischen: station?.quiz_shuffle === true,
    vorlesen: station?.quiz_tts === true,
    vorlesenKinder: station?.quiz_tts_kids === true,
  };
}

/** Felder fuer den Payload; ausserhalb einer Quiz-Station nichts. */
export function quizPayload(werte, istQuiz) {
  if (!istQuiz) return {};
  return {
    quiz_seconds: Math.round(Number(werte.sekunden)) || 0,
    quiz_seconds_kids: Math.round(Number(werte.sekundenKinder)) || 0,
    quiz_penalty: werte.abzug === true,
    quiz_penalty_kids: werte.abzugKinder === true,
    quiz_shuffle: werte.mischen === true,
    quiz_tts: werte.vorlesen === true,
    quiz_tts_kids: werte.vorlesenKinder === true,
  };
}

function Schalter({ checked, onChange, disabled, titel, hinweis }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? "opacity-60" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 accent-yellow-400"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span>
        <span className="text-sm text-gray-200">{titel}</span>
        {hinweis && <span className="block text-xs text-gray-400">{hinweis}</span>}
      </span>
    </label>
  );
}

/**
 * Schalter "vorlesen" plus Hoerprobe. Stimme und Tempo sind Einstellungen des
 * Geraets (Tablet: Zahnrad); die Probe klingt mit denen DIESES Geraets.
 */
function Vorlesen({ titel, an, onAn, kategorie, disabled }) {
  const [probe, setProbe] = useState(null);
  return (
    <div className="grid gap-2">
      <Schalter checked={an} onChange={onAn} disabled={disabled} titel={titel} />
      {an && vorlesenVerfuegbar() && (
        <div className="pl-7">
          <button
            type="button"
            onClick={() => {
              setProbe({ ok: true, text: "…" });
              vorlesenTesten(setProbe, { tempo: tempoFuer(kategorie) });
            }}
            className="px-3 py-2 rounded-lg bg-secondary-700 hover:bg-secondary-600 text-sm"
          >
            Probe hören
          </button>
        </div>
      )}
      {an && probe && (
        <p className={`pl-7 text-xs ${probe.ok ? "text-gray-400" : "text-red-300"}`}>{probe.text}</p>
      )}
    </div>
  );
}

export default function QuizFelder({ werte, onChange, disabled = false, zeigeHinweise = true }) {
  const setze = (feld) => (wert) => onChange({ ...werte, [feld]: wert });
  return (
    <div className="grid gap-3 rounded-lg border border-secondary-700 p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          type="number"
          label="Spielzeit Erwachsene (Sekunden)"
          value={werte.sekunden}
          onChange={(e) => setze("sekunden")(e.target.value)}
          min="10"
          max="3600"
          step="1"
          disabled={disabled}
        />
        <Input
          type="number"
          label="Spielzeit Kinder (Sekunden)"
          value={werte.sekundenKinder}
          onChange={(e) => setze("sekundenKinder")(e.target.value)}
          min="10"
          max="3600"
          step="1"
          disabled={disabled}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Schalter
          checked={werte.abzug}
          onChange={setze("abzug")}
          disabled={disabled}
          titel="Abzug für falsche Antworten – Erwachsene"
        />
        <Schalter
          checked={werte.abzugKinder}
          onChange={setze("abzugKinder")}
          disabled={disabled}
          titel="Abzug für falsche Antworten – Kinder"
        />
      </div>
      {zeigeHinweise && (
        <p className="text-xs text-gray-400 -mt-1">
          Wahr/Falsch: −1, vier Antworten: −⅓. Blindes Raten bringt dann im Schnitt nichts; am Tablet gibt es
          „Überspringen“. Nie unter 0 Punkte. Bei Kindern lieber aus, damit die Motivation oben bleibt.
        </p>
      )}
      <Schalter
        checked={werte.mischen}
        onChange={setze("mischen")}
        disabled={disabled}
        titel="Fragen für jedes Team in anderer Reihenfolge"
        hinweis={zeigeHinweise ? "Aus: alle Teams bekommen die Fragen in der gespeicherten Reihenfolge." : ""}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <Vorlesen
          titel="Fragen vorlesen – Erwachsene"
          an={werte.vorlesen}
          onAn={setze("vorlesen")}
          kategorie="Erwachsene"
          disabled={disabled}
        />
        <Vorlesen
          titel="Fragen vorlesen – Kinder"
          an={werte.vorlesenKinder}
          onAn={setze("vorlesenKinder")}
          kategorie="Kinder"
          disabled={disabled}
        />
      </div>
      {zeigeHinweise && (werte.vorlesen || werte.vorlesenKinder) && (
        <p className="text-xs text-gray-400 -mt-1">
          Das Tablet liest jede Frage (bei Mehrfachauswahl samt Antworten) mit seiner eingebauten Stimme
          vor. Der Countdown läuft dabei weiter – Spielzeit großzügig wählen. Auf dem Festplatz eine
          Bluetooth-Box anschließen. Stimme und Sprechtempo stellst du direkt am Tablet ein (Zahnrad).
        </p>
      )}
      {zeigeHinweise && (
        <p className="text-xs text-gray-400">
          Gewertet wird automatisch „höher ist besser“. Fragen, PIN und Versuche verwaltest du nach dem
          Speichern unter „Quiz“.
        </p>
      )}
    </div>
  );
}
